import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { BadgeIcon, CheckIcon, GridIcon, ShieldIcon, TimeIcon } from './icons';
import './styles.scss';

const plans = [
  {
    icon: BadgeIcon,
    index: '01',
    label: 'Plan · Free',
    title: 'Free forever',
    body: '$0 CAD. Up to 2 users, 1 active project, full feature access. Kick the tires as long as you like.',
  },
  {
    icon: GridIcon,
    index: '02',
    label: 'Plan · By Project',
    title: '$599 flat per project / mo',
    body: '50 active users included, unlimited free subcontractor accounts. A $5M renovation or a $200M tower, same monthly fee. No annual contracts.',
  },
  {
    icon: ShieldIcon,
    index: '03',
    label: 'Plan · By User',
    title: '$39 per user / mo',
    body: 'Unlimited active projects. Scale up or down monthly. Flat crosses over around 16 users.',
  },
  {
    icon: TimeIcon,
    index: '04',
    label: 'Plan · Trial',
    title: '90 days free',
    body: 'Three months on us. No credit card, no commitment, no demo gauntlet. On SafetyLoop? Try it free for 6 months.',
  },
];

const compareRows = [
  { pl: '$39/user/mo flat', legacy: '~$105/user/mo industry-cited equivalent' },
  { pl: '$599/project/mo flat', legacy: '1–3% of annual construction volume' },
  { pl: 'Month-to-month, cancel anytime', legacy: '12-month minimum, paid annually' },
  { pl: 'Unlimited subcontractor accounts', legacy: 'Varies by quote' },
  { pl: '90-day free trial, no credit card, no commitment', legacy: 'Demo-gated sales process' },
  { pl: 'Forms & inspections included', legacy: 'Add-on' },
  { pl: 'CAD pricing · Canadian data residency', legacy: 'USD' },
];

const checkPills = ['Month-to-month', 'No credit card', 'Cancel anytime'];

export default function PricingSection() {
  return (
    <section className="plh-pricing" id="compare">
      <div className="plh-pricing__head">
        <Reveal>
          <p className="plh-eyebrow">Pricing without the percent sign</p>
          <h2>
            <span className="plh-chapter">02 —</span> Pay for software.{' '}
            <span className="plh-accent">Not a percentage of your work.</span>
          </h2>
        </Reveal>
        <Reveal className="plh-pricing__explainer" delay={120}>
          <p>
            Procore doesn&rsquo;t publish list pricing — quotes commonly run 1&ndash;3% of your
            annual construction volume. ProjectLoop&rsquo;s pricing is on this page, in Canadian
            dollars, and it never scales with the value of your work.
          </p>
          <div className="plh-pricing__pills">
            {checkPills.map((pill) => (
              <span className="plh-check-pill" key={pill}>
                <CheckIcon />
                {pill}
              </span>
            ))}
          </div>
        </Reveal>
      </div>

      <div className="plh-plans">
        {plans.map((plan, idx) => {
          const Icon = plan.icon;
          return (
            <Reveal className="plh-plan" key={plan.label} delay={idx * 80}>
              <div className="plh-plan__top">
                <span className="plh-plan__icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="plh-plan__index" aria-hidden="true">
                  {plan.index}
                </span>
              </div>
              <p className="plh-plan__label plh-mono">{plan.label}</p>
              <h3>{plan.title}</h3>
              <p className="plh-plan__body">{plan.body}</p>
            </Reveal>
          );
        })}
      </div>

      <Reveal className="plh-pricing__ctas" delay={100}>
        <Link to="/pricing" className="pl-btn pl-btn--primary">
          See full pricing
        </Link>
        <Link to="/signup" className="pl-btn pl-btn--ghost">
          Start 90-day free trial
        </Link>
      </Reveal>

      <Reveal className="plh-compare" delay={80}>
        <div className="plh-compare__scroll">
          <table>
            <thead>
              <tr>
                <th className="plh-mono plh-compare__th-pl">ProjectLoop</th>
                <th className="plh-mono">Procore-class platforms</th>
              </tr>
            </thead>
            <tbody>
              {compareRows.map((row) => (
                <tr key={row.pl}>
                  <td className="plh-compare__pl">{row.pl}</td>
                  <td className="plh-compare__legacy">{row.legacy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Reveal>

      <div className="plh-pricing__proof">
        <Reveal className="plh-savings">
          <span className="plh-savings__num">$60K–$700K+</span>
          <p>
            Builders keep $60K&ndash;$700K+ per year versus percentage-of-volume platform quotes.
          </p>
          <Link to="/pricing" className="pl-btn pl-btn--outline-light">
            Run the numbers <span aria-hidden="true">→</span>
          </Link>
        </Reveal>
        <Reveal className="plh-quote" delay={100}>
          <span className="plh-quote__mark" aria-hidden="true">
            &ldquo;
          </span>
          <blockquote>
            ProjectLoop gives us a single view of tasks, inspections, and approvals. Our team
            finally has the clarity to keep projects on track — at a price that didn&rsquo;t need a
            board meeting.
          </blockquote>
          <p className="plh-quote__byline plh-mono">Director of Projects · Top 100 Contractor</p>
        </Reveal>
      </div>

      <Reveal as="p" className="plh-pricing__footnote plh-mono" delay={140}>
        Procore does not publish list pricing — your quote will vary. Estimates use industry-cited
        figures.
      </Reveal>
    </section>
  );
}
